/**
 * **Les sons d'un tour** — ce qu'une action qui vient de réussir fait entendre (SPEC-UI §7.6).
 *
 * ⟦A-76⟧ avait choisi, transcodé et déclaré les cinq derniers SFX de la liste fermée
 * (`skill_unlock`, `respec`, `cash_in`, `cash_out`, `sleep_transition`) — **sans appelant**.
 * Trois d'entre eux ne se rattachent à aucun écran en particulier : « mouvements de Clicks »
 * et « Dormir » sont des faits de l'ACTION, pas d'un composant. D'où ce module : une fonction
 * pure qui lit le type de l'action et le solde avant/après, et rend les ids à jouer.
 *
 * Les règles, et pourquoi :
 *
 * - **`sleep` ⇒ `sleep_transition`**, et rien d'autre : le loyer qui tombe pendant la nuit
 *   est un mouvement passif — c'est le récapitulatif ⟦A-16⟧ qui le raconte, pas une pièce qui
 *   tinte à chaque nuit. Même silence pour `laisser_tourner` (des jours qui passent).
 * - **`produce_video` / `video_jalon` ⇒ rien** : `publish_whoosh` possède ce moment, et le
 *   devis des embauches se lit sur l'écran de publication.
 * - **`acheter_skill` / `respec` ⇒ rien ici** : l'arbre joue `skill_unlock` / `respec`
 *   lui-même (§3.5) ; doubler d'un son de caisse brouillerait les deux.
 * - **Tout le reste** : le solde a bougé d'au moins un Click ⇒ `cash_in` ou `cash_out`. C'est
 *   la lecture la plus littérale de « mouvements de Clicks » : un achat en boutique, une
 *   thérapie, un sponsor accepté, une amende d'événement.
 *
 * ⟦A-22 · A-76⟧ **Le son ne décide jamais** : cette fonction LIT un état, elle n'en écrit
 * aucun, et aucun barème ne l'importe.
 */

import type { ActionType } from "@maxime/shared";

import type { SfxId } from "../components/SoundProvider";

/** Les actions dont le mouvement d'argent est passif ou déjà sonorisé ailleurs. */
export const ACTIONS_SANS_SON_DE_CAISSE: ReadonlySet<ActionType> = new Set<ActionType>([
  "sleep",
  "laisser_tourner",
  "produce_video",
  "video_jalon",
  "acheter_skill",
  "respec",
]);

/** En deçà, ce n'est pas un mouvement : un arrondi de jauge ne fait pas tinter la caisse. */
export const SEUIL_MOUVEMENT_CLICKS = 1;

/** Ce que l'action qui vient de RÉUSSIR fait entendre — dans l'ordre de lecture. */
export function sonsDuTour(
  action: ActionType,
  argentAvant: number,
  argentApres: number,
): SfxId[] {
  if (action === "sleep") return ["sleep_transition"];
  if (ACTIONS_SANS_SON_DE_CAISSE.has(action)) return [];
  const delta = argentApres - argentAvant;
  if (delta >= SEUIL_MOUVEMENT_CLICKS) return ["cash_in"];
  if (delta <= -SEUIL_MOUVEMENT_CLICKS) return ["cash_out"];
  return [];
}
